"use client";

import Image from "next/image";
import { Banner } from "@/components/shared/banner";
import { ContentRenderer } from "@/features/content/components/ContentRenderer";
import { CaseStudyReadCard } from "@/features/content/components/CaseStudyReadCard";
import CloudflareStreamVideo from "@/components/shared/CloudflareStreamVideo";
import { USER_TESTIMONIALS } from "@/constants/videos";
import { TableOfContents } from "@/features/marketing/components/blog/TableOfContents";
import ShareAndAuthorInfo from "@/features/marketing/components/blog/ShareAndAuthorInfo";
import { ContentItem } from "@/types/cms";
import { ContentBlock } from "@/features/editor/utils/content-blocks";

export interface ContentWrapperTocSubject {
    contentSections: Array<{
        heading: string;
        id: string;
    }>;
}

interface ContentWrapperLayoutProps {
    content: ContentItem;
    tocSubject: ContentWrapperTocSubject;
}

function isHeadingBlock(block: ContentBlock): block is Extract<ContentBlock, { type: "heading" }> {
    return block.type === "heading";
}

function isParagraphBlock(block: ContentBlock): block is Extract<ContentBlock, { type: "paragraph" }> {
    return block.type === "paragraph";
}

function isBulletListBlock(block: ContentBlock): block is Extract<ContentBlock, { type: "bullet-list" }> {
    return block.type === "bullet-list";
}

function isStatisticsBlock(block: ContentBlock): block is Extract<ContentBlock, { type: "statistics" }> {
    return block.type === "statistics";
}

function isQuoteBlock(block: ContentBlock): block is Extract<ContentBlock, { type: "quote" }> {
    return block.type === "quote";
}

function isVideoBlock(block: ContentBlock): block is Extract<ContentBlock, { type: "video" }> {
    return block.type === "video";
}

const LES_CASE_STUDY_SLUG = "university-of-birmingham-les";
const PERSPECTIVE_SLUGS = new Set([
    "ai-ethics-in-he",
    "science-behind-smarter-learning",
    "closing-teaching-learning-loop",
    "supporting-neurodiverse-learners-ai-edtech",
]);
const NEWS_SPLIT_SLUGS = new Set([
    "studystash-funding-round",
    "coventry-university",
    "edtechx-awards",
    "9-to-thrive-podcast",
    "innovators-uncensored",
]);
const CASE_STUDY_SPLIT_SLUGS = new Set(["wgs"]);
const FORCE_CASE_STUDY_SLUGS = new Set(["jcc"]);
const DEFAULT_EDITORIAL_INFO =
    "StudyStash builds evidence-informed learning tools for higher education.\nOur pilots focus on measurable outcomes for students and teaching teams.";

interface LesPilotDetail {
    label: string;
    value: string;
    logoSrc?: string;
    logoAlt?: string;
}

function capitalizeFirstWord(value: string): string {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function getOrdinalDay(day: number): string {
    const remainderTen = day % 10;
    const remainderHundred = day % 100;

    if (remainderTen === 1 && remainderHundred !== 11) return `${day}st`;
    if (remainderTen === 2 && remainderHundred !== 12) return `${day}nd`;
    if (remainderTen === 3 && remainderHundred !== 13) return `${day}rd`;
    return `${day}th`;
}

function formatPublishDate(value: string): string {
    const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!isoDateMatch) {
        return value;
    }

    const year = Number(isoDateMatch[1]);
    const month = Number(isoDateMatch[2]);
    const day = Number(isoDateMatch[3]);

    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return value;
    }

    const date = new Date(Date.UTC(year, month - 1, day));
    const monthLabel = new Intl.DateTimeFormat("en-GB", {
        month: "long",
        timeZone: "UTC",
    }).format(date);

    return `${getOrdinalDay(day)} ${monthLabel} ${year}`;
}

function getContentAuthorTitle(content: ContentItem): string | undefined {
    const authorName = content.author.name.trim().toLowerCase();

    const quoteWithMatchingAuthor = content.contentBlocks.find((block) => {
        if (!isQuoteBlock(block) || !block.title) {
            return false;
        }

        const quoteAuthor = block.author.trim().toLowerCase();
        return quoteAuthor === authorName || authorName.includes(quoteAuthor) || quoteAuthor.includes(authorName);
    });

    if (quoteWithMatchingAuthor && isQuoteBlock(quoteWithMatchingAuthor)) {
        return quoteWithMatchingAuthor.title;
    }

    return undefined;
}

function getLesPilotDetails(content: ContentItem) {
    const blocks = content.contentBlocks;
    const pilotHeadingIndex = blocks.findIndex(
        (block) => isHeadingBlock(block) && block.text === "UoB Pilot Background"
    );
    const pilotListBlock =
        pilotHeadingIndex >= 0 &&
        blocks[pilotHeadingIndex + 1] &&
        isBulletListBlock(blocks[pilotHeadingIndex + 1])
            ? (blocks[pilotHeadingIndex + 1] as Extract<ContentBlock, { type: "bullet-list" }>)
            : null;

    const details: LesPilotDetail[] = [
        {
            label: "Students reached",
            value: "5,000+ students",
        },
        {
            label: "Coverage",
            value: "All 5 UoB colleges",
        },
        {
            label: "Pilot window",
            value: "16 months (2023-2025)",
        },
        {
            label: "Institution",
            value: "University of Birmingham",
            logoSrc: "/uob_crest.png",
            logoAlt: "University of Birmingham crest",
        },
    ];

    if (!pilotListBlock) {
        return details;
    }

    return [
        {
            label: "Students reached",
            value: capitalizeFirstWord(
                pilotListBlock.items[0]?.replace(/\.$/, "").replace(" enrolled", "") || details[0].value
            ),
        },
        {
            label: "Coverage",
            value: capitalizeFirstWord(
                pilotListBlock.items[1]?.replace(/\.$/, "").replace("Implementation across ", "") || details[1].value
            ),
        },
        {
            label: "Pilot window",
            value: capitalizeFirstWord(
                pilotListBlock.items[2]
                    ?.replace(/\.$/, "")
                    .replace("Initial ", "")
                    .replace(" pilot from ", " (")
                    .replace(" to ", "-")
                    .concat(")") || details[2].value
            ),
        },
        details[3],
    ];
}

function LesTopKeyDetailsSection({ content }: { content: ContentItem }) {
    const details = getLesPilotDetails(content);

    return (
        <section className="mt-12 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
            <div className="grid gap-4 md:grid-cols-2">
                {details.map((detail) => (
                    <div key={detail.label} className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-600">
                            {detail.label}
                        </p>
                        <div className="inline-flex min-h-10 w-fit max-w-full items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5">
                            {detail.logoSrc ? (
                                <Image
                                    src={detail.logoSrc}
                                    alt={detail.logoAlt || detail.value}
                                    width={30}
                                    height={36}
                                    className="h-9 w-auto object-contain"
                                />
                            ) : null}
                            <span className="text-sm font-semibold leading-6 text-gray-900">{detail.value}</span>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function getLesDisplayBlocks(content: ContentItem): ContentBlock[] {
    const blocks = content.contentBlocks;
    const outcomesBlock = blocks.find(
        (block): block is Extract<ContentBlock, { type: "statistics" }> =>
            isStatisticsBlock(block) && block.layout === "outcome-cards"
    );
    const pilotHeadingIndex = blocks.findIndex(
        (block) => isHeadingBlock(block) && block.text === "UoB Pilot Background"
    );

    const filteredBlocks = blocks.filter((block, index) => {
        if (isHeadingBlock(block) && block.level === 1) {
            return false;
        }

        if (
            isParagraphBlock(block) &&
            (block.text.includes("University of Birmingham · College of Life and Environmental Sciences") ||
                block.text.includes("This perspective outlines how LES embedded StudyStash"))
        ) {
            return false;
        }

        if (outcomesBlock && block === outcomesBlock) {
            return false;
        }

        if (index === pilotHeadingIndex || index === pilotHeadingIndex + 1) {
            return false;
        }

        if (isHeadingBlock(block) && block.text === "Behaviour change intervention in practice") {
            return false;
        }

        return true;
    });

    if (!outcomesBlock) {
        return filteredBlocks;
    }

    const whatDidILearnIndex = filteredBlocks.findIndex(
        (block) =>
            isHeadingBlock(block) &&
            (block.text === "What did we learn" ||
                block.text === "What did we learn?" ||
                block.text === "What did I learn?")
    );

    const prePostHeading: Extract<ContentBlock, { type: "heading" }> = {
        type: "heading",
        level: 2,
        text: outcomesBlock.stats[0]?.title || "Pre/post using StudyStash",
    };

    const prePostStats: Extract<ContentBlock, { type: "statistics" }> = {
        ...outcomesBlock,
        stats: outcomesBlock.stats.map((stat) => ({
            ...stat,
            title: "",
        })),
    };

    if (whatDidILearnIndex === -1) {
        return [...filteredBlocks, prePostHeading, prePostStats];
    }

    const blocksWithPrePost = [...filteredBlocks];
    const nextH2AfterWhatDidILearn = blocksWithPrePost.findIndex(
        (block, index) => index > whatDidILearnIndex && isHeadingBlock(block) && block.level === 2
    );
    const insertionIndex =
        nextH2AfterWhatDidILearn === -1 ? blocksWithPrePost.length : nextH2AfterWhatDidILearn;

    blocksWithPrePost.splice(insertionIndex, 0, prePostHeading, prePostStats);

    return blocksWithPrePost;
}

export function getDisplayBlocks(content: ContentItem): ContentBlock[] {
    if (content.slug !== LES_CASE_STUDY_SLUG) {
        return content.contentBlocks;
    }

    return getLesDisplayBlocks(content);
}

function LesBodyContent({ content }: { content: ContentItem }) {
    const blocksToRender = getDisplayBlocks(content);
    const studentPerspectiveIndex = blocksToRender.findIndex(
        (block) => isHeadingBlock(block) && block.text.toLowerCase().includes("student perspective")
    );

    if (studentPerspectiveIndex === -1) {
        const contentBlocksWithoutVideo = blocksToRender.filter((block) => !isVideoBlock(block));

        return (
            <>
                <ContentRenderer blocks={contentBlocksWithoutVideo} />
                <div className="my-8">
                    <CloudflareStreamVideo
                        uid={USER_TESTIMONIALS.Tedi}
                        caption="Tedi Georgieva, BSc Medical Biochemistry"
                    />
                </div>
            </>
        );
    }

    const blocksBeforeStudentPerspective = blocksToRender.slice(0, studentPerspectiveIndex);
    const studentPerspectiveAndAfter = blocksToRender
        .slice(studentPerspectiveIndex)
        .filter((block) => !isVideoBlock(block));

    return (
        <>
            {blocksBeforeStudentPerspective.length > 0 ? (
                <ContentRenderer blocks={blocksBeforeStudentPerspective} />
            ) : null}
            <CaseStudyReadCard
                className="my-8 lg:max-w-none!"
                title="University of Birmingham"
                href="/about/case-studies/university-of-birmingham"
                imageSrc="/case-studies/eps.jpg"
                imageAlt="University of Birmingham campus"
                ctaText="Read more"
            />
            <ContentRenderer blocks={studentPerspectiveAndAfter} />
            <div className="my-8">
                <CloudflareStreamVideo
                    uid={USER_TESTIMONIALS.Tedi}
                    caption="Tedi Georgieva, BSc Medical Biochemistry"
                />
            </div>
        </>
    );
}

function SharedBanner({ content }: { content: ContentItem }) {
    const formattedPublishDate = formatPublishDate(content.publishDate);
    const authorTitle = getContentAuthorTitle(content);
    const isStudentSuccessStory = content.type === "case-study" && content.category === "success-story";
    const isPerspective = PERSPECTIVE_SLUGS.has(content.slug);
    const isNewsSplit = NEWS_SPLIT_SLUGS.has(content.slug);
    const isCaseStudySplit = CASE_STUDY_SPLIT_SLUGS.has(content.slug);
    const isForcedCaseStudy = FORCE_CASE_STUDY_SLUGS.has(content.slug);

    let bannerVariant: "article" | "article-compact" | "article-compact-split" = "article";
    let eyebrowLabel: string | undefined;

    if (isPerspective) {
        bannerVariant = "article-compact";
        eyebrowLabel = "Perspective";
    } else if (content.slug === "audible-business-attraction-program") {
        bannerVariant = "article-compact";
        eyebrowLabel = "News";
    } else if (content.slug === LES_CASE_STUDY_SLUG) {
        bannerVariant = "article-compact";
        eyebrowLabel = "Case study";
    } else if (content.type === "case-study") {
        if (isNewsSplit) {
            bannerVariant = "article-compact-split";
            eyebrowLabel = "News";
        } else if (isCaseStudySplit) {
            bannerVariant = "article-compact-split";
            eyebrowLabel = "Case study";
        } else if (isStudentSuccessStory && !isForcedCaseStudy) {
            bannerVariant = "article-compact-split";
            eyebrowLabel = "Student success story";
        } else {
            bannerVariant = "article-compact";
            eyebrowLabel = "Case study";
        }
    } else if (content.type === "blog" && isNewsSplit) {
        bannerVariant = "article-compact-split";
        eyebrowLabel = "News";
    }

    return (
        <div className="w-full">
            <Banner
                variant={bannerVariant}
                eyebrowLabel={eyebrowLabel}
                title={content.title}
                description={content.description}
                authorName={content.author.name}
                authorTitle={authorTitle}
                authorImage={content.author.image}
                publishDate={formattedPublishDate}
                readingTime={content.readingTime}
                articleImage={content.hero.image}
                articleImageAlt={content.hero.imageAlt}
				articleImageFit={content.slug === "educause-2026" ? "contain" : "cover"}
            />
        </div>
    );
}

function SharedAuthor({ content }: { content: ContentItem }) {
    const editorialInfo =
        content.slug === LES_CASE_STUDY_SLUG
            ? `${content.author.name} shares a lecturer perspective on embedding StudyStash in teaching practice, with a focus on behaviour change, motivation, and measurable student outcomes.`
            : DEFAULT_EDITORIAL_INFO;

    return (
        <ShareAndAuthorInfo
            authorName={content.author.name}
            authorImageUrl={content.author.image}
            editorialInfo={editorialInfo}
        />
    );
}

export function DefaultContentWrapperLayout({ content, tocSubject }: ContentWrapperLayoutProps) {
    const isLesCaseStudy = content.slug === LES_CASE_STUDY_SLUG;
    const hasToc = tocSubject.contentSections.length > 0;

    return (
        <div className="min-h-screen bg-gray-50">
            <SharedBanner content={content} />
            <div className="mx-auto w-full max-w-7xl px-5 pb-16 md:px-8 md:pb-20">
                <div className={hasToc ? "grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)] xl:gap-12" : "grid gap-8"}>
                    {hasToc ? (
                        <aside className="mt-12 hidden lg:block">
                            <div className="sticky top-8">
                                <TableOfContents subject={tocSubject} />
                            </div>
                        </aside>
                    ) : null}
                    <div className={hasToc ? "min-w-0" : "mx-auto w-full min-w-0 max-w-4xl"}>
                        {isLesCaseStudy ? <LesTopKeyDetailsSection content={content} /> : null}
                        <article className="prose mt-12 max-w-none lg:max-w-4xl">
                            {isLesCaseStudy ? (
                                <LesBodyContent content={content} />
                            ) : (
                                <ContentRenderer blocks={content.contentBlocks} />
                            )}
                        </article>
                        <div className="mt-8 lg:max-w-4xl">
                            <SharedAuthor content={content} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
